import {
  type DetectedCue,
  RECORDING_INSTRUCTION_MAX_LENGTH,
  RECORDING_INSTRUCTIONS_MAX,
} from "@rosc/contracts";
import {
  ListPlus,
  MessageSquarePlus,
  MessageSquareText,
  Pencil,
  X,
} from "lucide-react";
import type { JSX, ReactNode } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FieldDictationMic } from "@/components/ui/DictationButton";
import { V3Chip } from "@/components/ui/v3/chip";
import { useMacroShortcuts } from "@/hooks/visits/useMacroShortcuts";
import { cn } from "@/lib/utils";
import { useRecordingInstructionsStore } from "@/store/recordingInstructions.store";
import { singleLine, submitOnEnter, useAutoGrow } from "./instruction-field";

const CHIP =
  "inline-flex min-h-9 max-w-full items-center gap-2 rounded-full py-1.5 pl-3 pr-1.5 text-[14px] font-medium leading-5";
const CHIP_X =
  "flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring";

/**
 * "Add instruction" (v3.2): free text the note should follow, as grey chips,
 * plus a blue "Will insert" chip for each saved shortcut the clinician said
 * ("insert …") near the end. Both are applied once the note is written.
 * The card offers the clinician's macros (the AI assistant's list) to insert
 * with one click; a chip opens the card again to edit it, × deletes it
 * (clinician feedback 03/11).
 */
export const RecordingInstructions = ({
  cues = [],
  className,
  leading,
  dictation = false,
}: {
  // Shortcut cues heard live, not dismissed (empty without a live transcript).
  cues?: DetectedCue[];
  className?: string;
  // Shown before "Add instruction" on the same row (the template chip).
  leading?: ReactNode;
  // A dictation mic in the field (VITE_DICTATION_EVERYWHERE_ENABLED). Only
  // after Stop: while recording, the recorder owns the microphone and the
  // dictated words would land in the visit audio.
  dictation?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const items = useRecordingInstructionsStore((state) => state.items);
  const add = useRecordingInstructionsStore((state) => state.add);
  const remove = useRecordingInstructionsStore((state) => state.remove);
  const update = useRecordingInstructionsStore((state) => state.update);
  const skip = useRecordingInstructionsStore((state) => state.skip);
  const [open, setOpen] = useState(false);
  // The instruction being edited (its index), while the card shows it.
  const [editing, setEditing] = useState<number | null>(null);
  const full = items.length >= RECORDING_INSTRUCTIONS_MAX;
  const close = (): void => {
    setOpen(false);
    setEditing(null);
  };

  return (
    <section
      aria-label={t("recordingInstructions.title")}
      className={cn(
        "flex w-full flex-col items-start gap-2 font-v3",
        className,
      )}
      data-testid="recording-instructions"
    >
      {(cues.length > 0 || items.length > 0) && (
        <ul
          aria-label={t("recordingInstructions.listLabel")}
          className="flex max-w-full flex-wrap gap-2"
        >
          {cues.map((cue) => (
            <li
              key={`cue:${cue.trigger}`}
              className={cn(
                CHIP,
                "bg-v3-brand-tint text-v3-brand-strong shadow-[inset_0_0_0_1px_var(--v3-brand-ring)]",
              )}
            >
              <ListPlus aria-hidden className="h-4 w-4 shrink-0" />
              <span className="min-w-0 break-words">
                {t("recordingInstructions.willInsert", { name: cue.trigger })}
              </span>
              <button
                aria-label={t("recordingInstructions.dontInsert", {
                  name: cue.trigger,
                })}
                className={cn(CHIP_X, "hover:bg-v3-brand-tint-2")}
                type="button"
                onClick={() => {
                  skip(cue.trigger);
                }}
              >
                <X aria-hidden className="h-4 w-4" strokeWidth={2.25} />
              </button>
            </li>
          ))}
          {items.map((item, index) => (
            <li
              key={`item:${item}`}
              className={cn(
                CHIP,
                "bg-v3-surface-2 text-v3-text shadow-v3-hairline",
              )}
            >
              <button
                aria-label={t("recordingInstructions.edit", { text: item })}
                className="-my-1 -ml-1.5 flex min-h-9 min-w-0 items-center gap-2 rounded-full py-1 pl-1.5 pr-1 text-left transition-colors hover:text-v3-text-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
                data-testid="instruction-edit"
                type="button"
                onClick={() => {
                  setEditing(index);
                  setOpen(true);
                }}
              >
                <MessageSquareText
                  aria-hidden
                  className="h-4 w-4 shrink-0 text-v3-text-2"
                />
                <span className="min-w-0 break-words">{item}</span>
                <Pencil
                  aria-hidden
                  className="h-3.5 w-3.5 shrink-0 text-v3-text-3"
                />
              </button>
              <button
                aria-label={t("recordingInstructions.remove", { text: item })}
                className={cn(
                  CHIP_X,
                  "text-v3-text-2 hover:bg-v3-surface-3 hover:text-v3-text",
                )}
                type="button"
                onClick={() => {
                  remove(index);
                  if (editing !== null) close();
                }}
              >
                <X aria-hidden className="h-4 w-4" strokeWidth={2.25} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && leading}
      {open ? (
        <AddInstructionCard
          key={editing ?? "new"}
          dictation={dictation}
          initial={editing === null ? "" : (items[editing] ?? "")}
          mode={editing === null ? "add" : "edit"}
          others={items.filter((_, index) => index !== editing)}
          onAdd={(text) => {
            if (editing === null ? add(text) : update(editing, text)) close();
          }}
          onCancel={close}
        />
      ) : (
        (leading || !full) && (
          <div className="flex max-w-full flex-wrap items-center gap-2">
            {leading}
            {!full && (
              <button
                className="inline-flex h-10 items-center gap-2 rounded-full bg-v3-panel px-4 text-[14px] font-semibold text-v3-text shadow-v3-hairline-strong transition-colors hover:bg-v3-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
                type="button"
                onClick={() => {
                  setOpen(true);
                }}
              >
                <MessageSquarePlus aria-hidden className="h-[18px] w-[18px]" />
                {t("recordingInstructions.addButton")}
              </button>
            )}
          </div>
        )
      )}
    </section>
  );
};

const AddInstructionCard = ({
  dictation,
  initial,
  mode,
  others,
  onAdd,
  onCancel,
}: {
  dictation: boolean;
  initial: string;
  // "edit": the card changes an instruction already added.
  mode: "add" | "edit";
  // The other instructions: the text cannot repeat one of them.
  others: readonly string[];
  onAdd: (text: string) => void;
  onCancel: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [text, setText] = useState(initial);
  // The AI assistant's macros, one click each ("Insert <macro>").
  const macros = useMacroShortcuts();
  const input = useRef<HTMLTextAreaElement>(null);
  const inputId = useId();
  const helperId = useId();
  useEffect(() => {
    input.current?.focus();
  }, []);
  useAutoGrow(input, text);
  // The store refuses a repeat; the card says why instead of doing nothing
  // (staging check 03/11).
  const key = text.trim().toLowerCase();
  const duplicate = !!key && others.some((o) => o.toLowerCase() === key);
  const canSubmit = !!text.trim() && !duplicate;
  return (
    <form
      className="w-full rounded-[18px] bg-v3-panel p-4 shadow-v3-pop"
      data-testid="add-instruction-card"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onCancel();
        }
      }}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit) onAdd(text);
      }}
    >
      <FieldDictationMic
        enabled={dictation}
        fieldLabel={t("recordingInstructions.title")}
        getTarget={() => input.current}
      >
        {(mic) => (
          <>
            <div
              className={cn(
                "flex items-start gap-2 rounded-xl transition-shadow",
                mic?.listening && "ring-2 ring-v3-rec/40",
              )}
            >
              <label className="sr-only" htmlFor={inputId}>
                {t("recordingInstructions.addButton")}
              </label>
              <textarea
                ref={input}
                aria-describedby={helperId}
                aria-invalid={duplicate || undefined}
                autoComplete="off"
                className="block min-h-10 min-w-0 flex-1 resize-none overflow-hidden break-words bg-transparent py-2 text-[16px] leading-6 text-v3-text outline-none placeholder:text-v3-text-3"
                data-testid="add-instruction-field"
                enterKeyHint="done"
                id={inputId}
                maxLength={RECORDING_INSTRUCTION_MAX_LENGTH}
                placeholder={t("recordingInstructions.cardPlaceholder")}
                rows={1}
                value={text}
                onChange={(event) => {
                  setText(singleLine(event.target.value));
                }}
                onKeyDown={(event) => {
                  submitOnEnter(event, () => {
                    if (canSubmit) onAdd(text);
                  });
                }}
              />
              {mic?.button}
            </div>
            {mic?.status}
          </>
        )}
      </FieldDictationMic>
      <p
        aria-live="polite"
        className={cn(
          "mt-1 text-[13px] leading-[18px]",
          duplicate ? "font-medium text-v3-warn-text" : "text-v3-text-3",
        )}
        data-testid="add-instruction-helper"
        id={helperId}
      >
        {duplicate
          ? t("recordingInstructions.duplicate")
          : t("recordingInstructions.helper")}
      </p>
      {mode === "add" && macros.length > 0 && (
        <div className="mt-3">
          <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-v3-text-3">
            {t("recordingInstructions.macros")}
          </p>
          <ul
            aria-label={t("recordingInstructions.macros")}
            className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] lg:max-h-32 lg:flex-wrap lg:overflow-y-auto"
          >
            {macros.map((macro) => (
              <li key={macro.public_id} className="shrink-0">
                <V3Chip
                  data-testid="instruction-macro"
                  variant="suggestion"
                  onClick={() => {
                    onAdd(
                      t("v3.ask.insertShortcut", { name: macro.name.trim() }),
                    );
                  }}
                >
                  <ListPlus aria-hidden />
                  {macro.name}
                </V3Chip>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-3 flex items-center justify-end gap-1">
        <button
          className="h-10 rounded-full px-4 text-[14px] font-semibold text-v3-text-2 transition-colors hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
          type="button"
          onClick={onCancel}
        >
          {t("recordingInstructions.cancel")}
        </button>
        <button
          className="h-10 rounded-full bg-v3-ink px-5 text-[14px] font-semibold text-v3-on-ink transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:opacity-40"
          disabled={!canSubmit}
          type="submit"
        >
          {mode === "edit"
            ? t("recordingInstructions.saveAction")
            : t("recordingInstructions.addAction")}
        </button>
      </div>
    </form>
  );
};
